import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { TBrowserApiData } from '@/core/constants/event_types/additional_types';
import { TPlatformIdentifiers } from '@/core/constants/event_types/base_mable_event';

import { GenericDataHandler } from '../generic_data_handler';
import { StorageHandler } from '../storage_handler';

const defaultPlatformIdentifiers: TPlatformIdentifiers = {
  epik: [],
  fbclid: [],
  gacid: [],
  gbraid: [],
  gclid: [],
  ttclid: [],
  wbraid: [],
};
const addUnique = (array: string[], value: string) => {
  if (value && !array.includes(value)) array.push(value);
};

export class PlatformIdentifiersHandler {
  private static readonly _storageHandler =
    new StorageHandler<TPlatformIdentifiers>(
      'platformIdentifiers',
      defaultPlatformIdentifiers,
      EBrowserStorageType.LOCALSTORAGE,
    );
  private static readonly genericDataHandler =
    new GenericDataHandler<TPlatformIdentifiers>();

  static get(): TPlatformIdentifiers {
    return this._storageHandler.get();
  }

  static initOrLoadAndMaybeUpdate(
    browserData: TBrowserApiData,
    initialReferrer?: string,
  ): {
    platformIdentifiers: TPlatformIdentifiers;
    platformIdentifiersChanged: boolean;
  } {
    // remove existing invalid identifiers
    let removedInvalidPids = false;
    const existingIdentifiers = this.get();
    for (const key in existingIdentifiers) {
      const id = key as keyof TPlatformIdentifiers;
      if (Array.isArray(existingIdentifiers[id])) {
        const prevLength = existingIdentifiers[id].length;
        existingIdentifiers[id] = existingIdentifiers[id].filter(
          this.isValidIdentifier,
        );
        removedInvalidPids =
          removedInvalidPids || existingIdentifiers[id].length !== prevLength;
      }
    }
    this._storageHandler.set(existingIdentifiers);

    // initial referrer -> current referrer -> current location
    const { dataChanged: irChanged } = this.updateIdentifiersFromUrl(
      initialReferrer || '',
      {},
    );
    const { dataChanged: rlChanged } = this.updateIdentifiersFromUrl(
      browserData.rl,
      {},
    );
    const newIdentifiers = this._parseQueryParams(
      browserData.qpar,
      browserData.cks,
    );
    const currentIdentifiers = this.get();
    const { dataChanged, mergedData } = this.genericDataHandler.mergeData(
      currentIdentifiers,
      newIdentifiers,
    );
    this._storageHandler.set(mergedData);
    return {
      platformIdentifiers: mergedData,
      platformIdentifiersChanged:
        dataChanged || rlChanged || irChanged || removedInvalidPids,
    };
  }

  static updateFromIdentity(newIdentifiers: TPlatformIdentifiers): {
    platformIdentifiers: TPlatformIdentifiers;
    platformIdentifiersChanged: boolean;
  } {
    const { dataChanged, mergedData } = this.genericDataHandler.mergeData(
      this.get(),
      newIdentifiers,
    );

    this._storageHandler.set(mergedData);
    return {
      platformIdentifiers: mergedData,
      platformIdentifiersChanged: dataChanged,
    };
  }

  private static updateIdentifiersFromUrl(
    url: string,
    cookies: Record<string, string> = {},
  ) {
    const currentIdentifiers = this.get();
    try {
      const urlParams = new URL(url).searchParams;
      const qpar: Record<string, string[]> = {};
      for (const key of urlParams.keys()) {
        qpar[key] = urlParams.getAll(key);
      }
      const newIdentifiers = this._parseQueryParams(qpar, cookies);
      const result = this.genericDataHandler.mergeData(
        currentIdentifiers,
        newIdentifiers,
      );
      this._storageHandler.set(result.mergedData);
      return result;
    } catch {
      return { dataChanged: false, mergedData: currentIdentifiers };
    }
  }

  private static _parseQueryParams(
    qpar: Record<string, string[]>,
    cookies: Record<string, string>,
  ): TPlatformIdentifiers {
    const identifiers = JSON.parse(
      JSON.stringify({ ...defaultPlatformIdentifiers, ...this.get() }),
    ) as TPlatformIdentifiers;
    qpar ||= {};
    if (!qpar || Object.keys(qpar).length === 0) return identifiers;
    Object.entries(qpar).forEach(([key, value]) => {
      const firstValue = value.flat()[0]; // Always take the first entry in the array
      if (!this.isValidIdentifier(firstValue)) return;
      if ((key === 'fbclid' || key === '_fbclid') && firstValue)
        addUnique(identifiers.fbclid, firstValue);
      if ((key === 'ttclid' || key === '_ttclid') && firstValue)
        addUnique(identifiers.ttclid, firstValue);
      if ((key === 'epik' || key === '_epik') && firstValue)
        addUnique(identifiers.epik, firstValue);
      if ((key === 'gclid' || key === '_gclid') && firstValue)
        addUnique(identifiers.gclid, firstValue);
      if ((key === 'gbraid' || key === '_gbraid') && firstValue)
        addUnique(identifiers.gbraid, firstValue);
      if ((key === 'wbraid' || key === '_wbraid') && firstValue)
        addUnique(identifiers.wbraid, firstValue);
      // if (key === 'gacid') addUnique(identifiers.gacid, firstValue); not used currently
    });

    // GCLID various cases
    this.addGclidFromCookies(identifiers, cookies);

    // get the _ga cookie value - e.g. "GA1.2.987654321.0123456789" take everything after the second dot 987654321.0123456789
    const gaCookie = cookies?.['_ga'];
    if (gaCookie) {
      identifiers.gacid = identifiers.gacid || [];
      const gaCid = gaCookie.split('.').slice(2).join('.');
      if (gaCid && gaCid.length > 0) {
        addUnique(identifiers.gacid, gaCookie);
      }
    }

    return identifiers;
  }

  private static isValidIdentifier(value?: string): boolean {
    return !!value && value.trim().length > 0 && value != value.toLowerCase();
  }

  private static addGclidFromCookies(
    identifiers: TPlatformIdentifiers,
    cookies: Record<string, string>,
  ) {
    const gclids: { timestamp: number; value: string }[] = [];

    // _gcl_aw
    const gclAw = cookies?.['_gcl_aw'];
    if (gclAw) {
      const gclid = gclAw.split('.')[2];
      const ts = Number(gclAw.split('.')[1]);
      if (gclid && !Number.isNaN(ts)) {
        gclids.push({ timestamp: ts, value: gclid });
      }
    }

    // _gcl_dc
    const gclDc = cookies?.['_gcl_dc'];
    if (gclDc) {
      const gclid = gclDc.split('.')[2];
      const ts = Number(gclDc.split('.')[1]);
      if (gclid && !Number.isNaN(ts)) {
        gclids.push({ timestamp: ts, value: gclid });
      }
    }

    gclids.sort((a, b) => b.timestamp - a.timestamp);
    for (const gclid of gclids) {
      if (this.isValidIdentifier(gclid.value))
        addUnique(identifiers.gclid, gclid.value);
    }
  }

  static clear() {
    this._storageHandler.set(defaultPlatformIdentifiers);
  }
}
